<template>
  <div>
    <b-card>
      <h4 class="mb-1">
        Claude AI Reporting
      </h4>

      <label class="font-weight-bold">Server address</label>
      <b-input-group class="mb-2">
        <b-form-input
          :value="mcpUrl"
          readonly
        />
        <b-input-group-append>
          <b-button
            variant="primary"
            :disabled="!mcpUrl"
            @click="copy"
          >
            {{ copied ? 'Copied' : 'Copy' }}
          </b-button>
        </b-input-group-append>
      </b-input-group>

      <h5 class="mb-50">
        How to connect
      </h5>
      <ol class="mb-0">
        <li>In Claude, open Settings, then Connectors, then Add custom connector.</li>
        <li>Paste the server address above and save.</li>
        <li>Approve access when Curfox opens. Sign in if asked.</li>
      </ol>
    </b-card>

    <b-card>
      <h5 class="mb-1">
        Connections
      </h5>
      <b-overlay
        :show="loading"
        rounded="sm"
      >
        <p
          v-if="!connections.length && !loading"
          class="text-muted mb-0"
        >
          No connections yet. Follow the steps above to connect Claude.
        </p>

        <b-table-simple
          v-else
          responsive
          hover
          small
        >
          <b-thead>
            <b-tr>
              <b-th>Added Date</b-th>
              <b-th>Added By</b-th>
              <b-th>Connection</b-th>
              <b-th>Last Update</b-th>
              <b-th>Status</b-th>
              <b-th>Action</b-th>
            </b-tr>
          </b-thead>
          <b-tbody>
            <b-tr
              v-for="c in connections"
              :key="c.grant_id"
            >
              <b-td>{{ formatDate(c.connected_at) }}</b-td>
              <b-td>{{ addedBy }}</b-td>
              <b-td>{{ c.client_name || 'Claude' }}</b-td>
              <b-td>{{ formatDate(c.revoked_at || c.last_used_at) }}</b-td>
              <b-td>
                <b-badge :variant="c.status === 'active' ? 'success' : 'secondary'">
                  {{ c.status === 'active' ? 'Connected' : 'Disconnected' }}
                </b-badge>
              </b-td>
              <b-td>
                <b-button
                  v-if="c.status === 'active'"
                  size="sm"
                  variant="danger"
                  @click="revokeTarget = c"
                >
                  Disconnect
                </b-button>
                <span
                  v-else
                  class="text-muted"
                >No action needed</span>
              </b-td>
            </b-tr>
          </b-tbody>
        </b-table-simple>
      </b-overlay>
    </b-card>

    <b-modal
      :visible="!!revokeTarget"
      title="Disconnect Claude?"
      ok-title="Disconnect"
      ok-variant="danger"
      cancel-variant="secondary"
      centered
      @ok="confirmRevoke"
      @hidden="revokeTarget = null"
    >
      <p class="mb-0">
        This connection will stop working immediately. You can reconnect at any time.
      </p>
    </b-modal>
  </div>
</template>

<script>
import {
  BBadge, BButton, BCard, BFormInput, BInputGroup, BInputGroupAppend,
  BModal, BOverlay, BTableSimple, BTbody, BTd, BTh, BThead, BTr,
} from 'bootstrap-vue'
import mcpRepository from '@/repository/mcpRepository'
import SuccessMessage from '@core/mixins/SuccessMessage'
import ErrorMessage from '@core/mixins/ErrorMessage'
import { apiUrl } from '@/constants/config'

export default {
  components: {
    BBadge,
    BButton,
    BCard,
    BFormInput,
    BInputGroup,
    BInputGroupAppend,
    BModal,
    BOverlay,
    BTableSimple,
    BTbody,
    BTd,
    BTh,
    BThead,
    BTr,
  },
  mixins: [SuccessMessage, ErrorMessage],
  data() {
    return {
      connections: [],
      loading: false,
      copied: false,
      revokeTarget: null,
    }
  },
  computed: {
    addedBy() {
      try {
        const u = JSON.parse(localStorage.getItem('current_user')) || {}
        const name = `${u.first_name || ''} ${u.last_name || ''}`.trim()
        return name || u.email || '—'
      } catch (e) {
        return '—'
      }
    },
    mcpUrl() {
      // The MCP server lives on the API host, at the root (not under /api).
      return apiUrl ? `${apiUrl.replace(/\/api\/?$/, '')}/mcp` : ''
    },
  },
  mounted() {
    this.loadConnections()
  },
  methods: {
    formatDate(value) {
      if (!value) return '—'
      const d = new Date(value.replace(' ', 'T') + (value.includes('T') ? '' : 'Z'))
      return Number.isNaN(d.getTime()) ? value : d.toLocaleString()
    },
    async loadConnections() {
      this.loading = true
      try {
        this.connections = (await mcpRepository.getConnections()).data.data
      } catch (e) {
        this.showErrorMessage('Could not load connections. Refresh the page to try again.')
      } finally {
        this.loading = false
      }
    },
    async confirmRevoke() {
      const c = this.revokeTarget
      if (!c) return
      try {
        await mcpRepository.revokeConnection(c.grant_id)
        this.showSuccessMessage('Connection disconnected')
        this.loadConnections()
      } catch (e) {
        this.showErrorMessage('Could not disconnect. Try again.')
      } finally {
        this.revokeTarget = null
      }
    },
    copy() {
      if (!this.mcpUrl || !navigator.clipboard) return
      navigator.clipboard.writeText(this.mcpUrl)
      this.copied = true
      setTimeout(() => { this.copied = false }, 2000)
    },
  },
}
</script>
